import { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
  Rocket, BarChart3, Layers, FolderOpen, LayoutDashboard, PhoneCall,
  LifeBuoy, CreditCard, GraduationCap, Loader2, Clock, LogOut, Menu, X,
  CalendarCheck, Workflow, Handshake, Users,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusPill } from "../../components/site/Reveal";

const NAV = [
  { to: "/hub", end: true, label: "Start Here", icon: Rocket },
  { to: "/hub/day-1", label: "Day 1 — Getting Started", icon: CalendarCheck },
  { to: "/hub/progress", label: "My Progress", icon: BarChart3 },
  { to: "/hub/modules", label: "Blueprint Modules", icon: Layers },
  { to: "/hub/templates", label: "Template Vault", icon: FolderOpen },
  { to: "/hub/dashboard", label: "Implementation Dashboard", icon: LayoutDashboard },
  { to: "/hub/pipeline-engine", label: "Pipeline Engine", icon: Workflow },
  { to: "/hub/calls", label: "Coaching Calls", icon: PhoneCall },
  { to: "/hub/help", label: "Ask for Help", icon: LifeBuoy },
  { to: "/hub/partner", label: "Partner Program", icon: Handshake },
  { to: "/hub/team-onboarding", label: "Team Onboarding", icon: Users },
  { to: "/hub/account", label: "Account & Billing", icon: CreditCard },
  { to: "/hub/graduation", label: "Graduation / Next Steps", icon: GraduationCap },
];

function Shell({ children, user, logout, navigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-navy pt-16 flex" data-testid="hub-shell">
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-charcoal border-r border-white/10 pt-20 lg:pt-6 px-4 flex flex-col transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-3 pb-5 border-b border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-safety">[WORKBASE HUB]</p>
          <p className="text-white font-display font-bold mt-1.5 truncate">{user.name || user.email}</p>
          <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">
            {user.role === "admin" ? "Admin Access" : "Active Member"}
          </p>
        </div>
        <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto dark-scroll" data-testid="hub-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              data-testid={`hub-nav-${n.label.toLowerCase().replace(/[\s/&]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-safety/10 text-safety border-l-2 border-safety" : "text-slate-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                }`
              }>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="hub-logout-btn"
          className="flex items-center gap-3 px-3 py-3 text-sm text-slate-500 hover:text-white border-t border-white/10 transition-colors">
          <LogOut size={16} /> Sign out
        </button>
      </aside>
      <button onClick={() => setOpen(!open)} data-testid="hub-mobile-toggle" aria-label="Toggle hub menu"
        className="lg:hidden fixed bottom-5 right-5 z-50 w-12 h-12 bg-safety text-white grid place-items-center shadow-xl">
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>
      <main className="flex-1 min-w-0 px-5 sm:px-8 py-8 lg:py-10">{children}</main>
    </div>
  );
}

function ApplicationPending({ user }) {
  return (
    <div className="min-h-screen bg-navy pt-16 flex items-center blueprint-grid grain" data-testid="application-pending">
      <div className="max-w-lg mx-auto px-5 text-center">
        <Clock size={48} className="text-safety mx-auto" />
        <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl mt-6">Application in review.</h1>
        <div className="mt-6 flex justify-center"><StatusPill status={user.application_status || "New"} /></div>
        <p className="text-slate-400 mt-6 leading-relaxed">
          We review every application by hand within 2 business days. You'll get an email
          the moment there's a decision — and if accepted, this page becomes your
          enrollment checkout.
        </p>
      </div>
    </div>
  );
}

function Paywall() {
  const [busy, setBusy] = useState("");
  const pay = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout unavailable — contact us.");
      setBusy("");
    }
  };
  return (
    <div className="min-h-screen bg-navy pt-16 flex items-center blueprint-grid grain" data-testid="paywall">
      <div className="max-w-2xl mx-auto px-5 w-full">
        <div className="bg-charcoal border border-safety/40 p-8 sm:p-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-safety text-center">[STATUS: ACCEPTED — PENDING PAYMENT]</p>
          <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl mt-5 text-center">You're in. Lock your seat.</h1>
          <p className="text-slate-400 mt-4 leading-relaxed text-center">
            Your application was accepted into the Founding Cohort. Choose how you'd like
            to enroll — WorkBase Hub unlocks as soon as your first payment clears.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <div className="border border-white/15 bg-navy p-6 flex flex-col" data-testid="paywall-option-full">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">[PAY IN FULL]</p>
              <p className="font-display font-extrabold text-white text-4xl mt-3">$497</p>
              <p className="text-slate-400 text-sm mt-1">one-time payment</p>
              <button onClick={() => pay("full")} disabled={!!busy} data-testid="paywall-checkout-full-btn"
                className="mt-6 bg-safety hover:bg-safety-hover disabled:opacity-50 text-white font-semibold py-3.5 transition-colors">
                {busy === "full" ? "Opening secure checkout…" : "Pay in full — $497"}
              </button>
            </div>
            <div className="border border-white/15 bg-navy p-6 flex flex-col" data-testid="paywall-option-plan">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">[PAYMENT PLAN]</p>
              <p className="font-display font-extrabold text-white text-4xl mt-3">$275</p>
              <p className="text-slate-400 text-sm mt-1">× 2 payments, collected monthly</p>
              <button onClick={() => pay("plan")} disabled={!!busy} data-testid="paywall-checkout-plan-btn"
                className="mt-6 border border-safety text-safety hover:bg-safety hover:text-white disabled:opacity-50 font-semibold py-3.5 transition-colors">
                {busy === "plan" ? "Opening secure checkout…" : "Start plan — $275 today"}
              </button>
            </div>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-6 text-center">14-day fit guarantee applies to both options</p>
        </div>
      </div>
    </div>
  );
}

export default function HubLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [progress, setProgress] = useState(null);

  const loadProgress = useCallback(async () => {
    const { data } = await api.get("/hub/progress");
    setProgress(data);
  }, []);

  const isMember = user && (user.role === "active_member" || user.role === "admin");

  useEffect(() => {
    if (!isMember) return;
    api.get("/hub/content").then((r) => setContent(r.data)).catch(() => {});
    loadProgress().catch(() => {});
  }, [isMember, loadProgress]);

  const toggle = useCallback(async (item_id, completed) => {
    const { data } = await api.post("/hub/progress", { item_id, completed });
    setProgress(data);
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-navy grid place-items-center pt-16"><Loader2 className="text-safety animate-spin" size={36} /></div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "applicant") return <ApplicationPending user={user} />;
  if (user.role === "accepted_pending_payment") return <Paywall />;
  if (!content || !progress) {
    return <div className="min-h-screen bg-navy grid place-items-center pt-16"><Loader2 className="text-safety animate-spin" size={36} /></div>;
  }
  return (
    <Shell user={user} logout={logout} navigate={navigate}>
      <Outlet context={{ content, progress, toggle, user }} />
    </Shell>
  );
}
